﻿import {
  subscribe,
  getState,
  getCharacterById,
  closeModal as closeModalState,
  setModalEdit,
  updateCharacterLocal,
  removeCharacterLocal,
  addLevelUpEventLocal,
  setLoading,
  normalizeIdentityKey
} from '../state/store.js';
import { updateCharacter, deleteCharacter, uploadPortrait, createLevelUpEvent } from '../services/supabase.js';
import { isInTimeJail, formatTimeJailDisplay } from '../utils/time-jail.js';
import { formatDateForDisplayValue, formatDateForInputValue } from '../utils/dates.js';
import { getCareerLengthDays, formatCareerLength } from '../utils/career.js';
import { getHighestCharacterLevel } from '../utils/levels.js';
import { RACE_OPTIONS, renderSingleSelectPills } from '../utils/character-options.js';
import { getLocationOptions } from '../utils/locations.js';
import { getPlayerByName, getPlayerOptions } from '../utils/players.js';

const modalEl = document.getElementById('characterModal');
const modalForm = modalEl ? modalEl.querySelector('.modal-form') : null;
const modalName = document.getElementById('modalName');
const modalPlayer = document.getElementById('modalPlayer');
const modalOverview = document.getElementById('modalOverview');
const modalStats = document.getElementById('modalStats');
const modalBackground = document.getElementById('modalBackground');
const modalEditBtn = document.getElementById('modalEditBtn');
const modalCancelEditBtn = document.getElementById('modalCancelEditBtn');
const modalCloseBtn = document.getElementById('modalCloseBtn');
const modalEditBar = document.getElementById('modalEditBar');
const modalDangerBar = modalEl ? modalEl.querySelector('.modal-danger-bar') : null;
const modalDeleteCharacterBtn = document.getElementById('modalDeleteCharacterBtn');
const modalPortraitPlate = document.getElementById('modalPortraitPlate');
const modalPortraitInput = document.getElementById('modalPortraitInput');
const modalPortraitUpload = document.getElementById('modalPortraitUpload');

let currentCharacterId = null;
let currentPortraitUrl = '';

const isDeadStatus = (status) => String(status || '').trim().toLowerCase() === 'dead';

const canEditCharacter = (character) => {
  if (!character) return false;
  const state = getState();
  if (state.viewMode === 'dm') return true;
  if (state.viewMode === 'player' && state.identity && !state.identity.isGuest) {
    return normalizeIdentityKey(character.player) === normalizeIdentityKey(state.identity.name);
  }
  return false;
};

const setPlateImage = (plateEl, url) => {
  if (!plateEl) return;
  if (url) {
    plateEl.style.backgroundImage = `url("${url}")`;
    plateEl.classList.add('has-image');
  } else {
    plateEl.style.backgroundImage = '';
    plateEl.classList.remove('has-image');
  }
};

const setBodyScrollLock = (locked) => {
  document.body.classList.toggle('modal-open', locked);
};

const CLASS_OPTIONS = [
  'Fighter',
  'Magic-User',
  'Cleric',
  'Thief',
  'Ranger',
  'Paladin',
  'Druid',
  'Monk',
  'Illusionist',
  'Assassin',
  'Bard',
  'Barbarian'
];

const LANGUAGE_OPTIONS = [
  'Common',
  'Druidic',
  'Dwarvish',
  'Elvish',
  'Giant',
  'Gnomish',
  'Goblin',
  'Halfling',
  'Hobgoblin',
  'Kobold',
  'Lizardman',
  'Orcish',
  'Orgish',
  "Thieves' cant",
  'Undercommon'
];

const SECONDARY_SKILL_OPTIONS = [
  'Armorer',
  'Barterer',
  'Bowyer',
  'Cabinetmaker',
  'Carpenter',
  'Farmer',
  'Fisher (Hook and line)',
  'Fisher (Net)',
  'Fletcher',
  'Forester',
  'Freighter',
  'Furrier',
  'Gambler',
  'Gardener',
  'Hunter',
  'Husbandman',
  'Jeweler',
  'Lapidary',
  'Leather Worker',
  'Limner',
  'Mason',
  'Miner',
  'Navigator (fresh or salt)',
  'None of worth',
  'Painter',
  'Sailer (fresh or salt)',
  'Shipwright (boats or ships)',
  'Tailor',
  'Tanner',
  'Teamster',
  'Trader',
  'Trapper',
  'Weaver',
  'Woodworker'
];

const ALIGNMENT_OPTIONS = [
  'Lawful Good',
  'Chaotic Good',
  'Neutral Good',
  'True Neutral',
  'Lawful Evil',
  'Chaotic Evil',
  'Neutral Evil',
  'Lawful Neutral',
  'Chaotic Neutral'
];

const STATUS_OPTIONS = ['Alive', 'Dead', 'Enslaved'];

const OVERVIEW_FIELDS = [
  { key: 'character_name', label: 'Character Name', accessor: (c) => c.name },
  { key: 'player', label: 'Player', accessor: (c) => c.player, playerSelect: true, multiple: false },
  {
    key: 'status',
    label: 'Status',
    accessor: (c) => c.status,
    pills: STATUS_OPTIONS,
    multiple: false,
    badgeDisplay: false
  },
  { key: 'race', label: 'Race', accessor: (c) => c.race, selectOptions: RACE_OPTIONS, multiple: false },
  { key: 'class', label: 'Class', accessor: (c) => c.class, pills: CLASS_OPTIONS },
  { key: 'level', label: 'Level', accessor: (c) => c.level, placeholder: '3 or 3/1/2' },
  { key: 'hp', label: 'Total HP', accessor: (c) => c.hp },
  {
    key: 'current_location',
    label: 'Location',
    accessor: (c) => c.current_location,
    locationSelect: true,
    multiple: false
  },
  { key: 'time_jail', label: 'Time Jail', accessor: (c) => c.time_jail, date: true }
];

const STAT_FIELDS = [
  { key: 'str', label: 'STR', placeholder: '18 or 18/96' },
  { key: 'dex', label: 'DEX' },
  { key: 'con', label: 'CON' },
  { key: 'int', label: 'INT' },
  { key: 'wis', label: 'WIS' },
  { key: 'cha', label: 'CHA' }
];

const BACKGROUND_FIELDS = [
  { key: 'alignment', label: 'Alignment', pills: ALIGNMENT_OPTIONS, multiple: false },
  { key: 'deity', label: 'Deity' },
  { key: 'languages', label: 'Languages', pills: LANGUAGE_OPTIONS },
  {
    key: 'secondary_skills',
    label: 'Secondary Skills',
    pills: SECONDARY_SKILL_OPTIONS,
    multiple: true,
    limit: 2
  },
  { key: 'special_interest', label: 'Special Interest' },
  { key: 'birthday', label: 'Birthday', date: true },
  { key: 'age', label: 'Age' },
  { key: 'birth_location', label: 'Birth Location', locationSelect: true, multiple: false },
  { key: 'first_adventure', label: 'First Adventure', date: true },
  { key: 'deathday', label: 'Deathday', date: true },
  {
    key: 'career_length',
    label: 'Career Length',
    accessor: (c) => formatCareerLength(getCareerLengthDays(c)),
    editDisabled: true
  },
  { key: 'cause_of_death', label: 'Cause of Death', multiline: true }
];

const escapeAttr = (value = '') =>
  String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/"/g, '&quot;');

const escapeHTML = (value = '') =>
  String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');

const getDisplayValue = (field, value) => {
  if (field.key === 'time_jail') return formatTimeJailDisplay(value) || '';
  if (field.date) return formatDateForDisplayValue(value);
  return value;
};

const parsePillValues = (value = '') =>
  new Set(
    String(value || '')
      .split(',')
      .map((v) => v.trim())
      .filter(Boolean)
  );

const parseSinglePillValue = (value = '') => {
  const trimmed = String(value || '').trim();
  return trimmed ? new Set([trimmed]) : new Set();
};

const pillSummaryText = (label, set, multiple = true) =>
  multiple ? (set.size ? `${set.size} selected` : `Select ${label}`) : set.size ? Array.from(set)[0] : `Select ${label}`;

const updatePillSummaryDisplay = (container, set) => {
  if (!container) return;
  const summaryLabel = container.querySelector('.pill-summary-label');
  const label = container.dataset.pillLabel || 'Classes';
  const multiple = container.dataset.pillMode !== 'single';
  if (summaryLabel) summaryLabel.textContent = pillSummaryText(label, set, multiple);
  container
    .querySelectorAll('.pill')
    .forEach((pill) => {
      const value = pill.getAttribute('data-pill-value');
      pill.classList.toggle('pill-active', value && set.has(value));
    });
};

const syncAllPillSummaries = () => {
  const root = modalForm || modalEl;
  if (!root) return;
  root.querySelectorAll('[data-pill-container]').forEach((container) => {
    const hidden = container.querySelector('input[type="hidden"][data-field]');
    const multiple = container.dataset.pillMode !== 'single';
    const rawValue = hidden ? hidden.value : '';
    const set = multiple ? parsePillValues(rawValue) : parseSinglePillValue(rawValue);
    updatePillSummaryDisplay(container, set);
  });
};

const renderGrid = (container, fields, character, editable) => {
  container.innerHTML = fields
    .map((field) => {
      const value =
        typeof field.accessor === 'function' ? field.accessor(character) : character[field.key] || '';
      if (editable) {
        if (field.editDisabled) return '';
        const safeAttr = escapeAttr(value);
        const safeText = escapeHTML(value);
        const extraClass = field.multiline ? 'full-width' : '';
        if (field.multiline) {
          return `<div class="${extraClass}">
          <label class="label">${field.label}</label>
          <textarea data-field="${field.key}">${safeText}</textarea>
        </div>`;
        }
        if (field.pills && Array.isArray(field.pills)) {
          const multiple = field.multiple !== false;
          const selectedSet = multiple ? parsePillValues(value) : parseSinglePillValue(value);
          const pills = field.pills
            .map((opt) => {
              const active = selectedSet.has(opt);
              return `<button type="button" class="pill ${active ? 'pill-active' : ''}" data-pill-field="${field.key}" data-pill-value="${opt}">
                ${opt}
              </button>`;
            })
            .join('');
          const hiddenValue = multiple
            ? escapeAttr(
                Array.from(selectedSet)
                  .map((x) => x.trim())
                  .filter(Boolean)
                  .join(', ')
              )
            : escapeAttr((selectedSet.size ? Array.from(selectedSet)[0] : '') || '');
          const summaryText = pillSummaryText(field.label, selectedSet, multiple);
          const pillLabelAttr = escapeAttr(field.label);
          const modeAttr = multiple ? 'multiple' : 'single';
          const limitAttr = field.limit ? ` data-pill-limit="${field.limit}"` : '';
          return `<div class="${extraClass}" data-pill-container="${field.key}" data-pill-label="${pillLabelAttr}" data-pill-mode="${modeAttr}"${limitAttr}>
            <label class="label">${field.label}</label>
            <div class="pill-dropdown">
              <button type="button" class="pill-dropdown-summary" data-pill-summary>
                <span class="pill-summary-label">${summaryText}</span>
                <span class="pill-chevron" aria-hidden="true"></span>
              </button>
              <div class="pill-list" hidden>
                ${pills}
              </div>
            </div>
            <input type="hidden" data-field="${field.key}" value="${hiddenValue}">
          </div>`;
        }
        const singleSelectOptions = field.playerSelect
          ? getPlayerOptions(getState(), value)
          : field.locationSelect
            ? getLocationOptions(getState(), value)
            : Array.isArray(field.selectOptions)
              ? field.selectOptions
              : null;
        if (singleSelectOptions) {
          const selectedSet = parseSinglePillValue(value);
          const summaryText = pillSummaryText(field.label, selectedSet, false);
          const hiddenValue = escapeAttr((selectedSet.size ? Array.from(selectedSet)[0] : '') || '');
          return `<div class="${extraClass}" data-pill-container="${field.key}" data-pill-label="${escapeAttr(field.label)}" data-pill-mode="single">
            <label class="label">${field.label}</label>
            <div class="pill-dropdown">
              <button type="button" class="pill-dropdown-summary" data-pill-summary>
                <span class="pill-summary-label">${summaryText}</span>
                <span class="pill-chevron" aria-hidden="true"></span>
              </button>
              <div class="pill-list" hidden>
                ${renderSingleSelectPills(singleSelectOptions, value, { fieldKey: field.key })}
              </div>
            </div>
            <input type="hidden" data-field="${field.key}" value="${hiddenValue}">
          </div>`;
        }
        if (field.date) {
          return `<div class="${extraClass}">
          <label class="label">${field.label}</label>
          <input type="date" data-field="${field.key}" value="${formatDateForInputValue(value)}">
        </div>`;
        }
        return `<div class="${extraClass}">
          <label class="label">${field.label}</label>
          <input data-field="${field.key}" value="${safeAttr}"${field.placeholder ? ` placeholder="${escapeAttr(field.placeholder)}"` : ''}>
        </div>`;
      }
      const displayValue = getDisplayValue(field, value);
      if (field.pills && field.badgeDisplay !== false) {
        const chips = (displayValue || '')
          .split(',')
          .map((v) => v.trim())
          .filter(Boolean);
        const content = chips.length
          ? `<div class="pill-badge-group">${chips
              .map((chip) => `<span class="badge">${escapeHTML(chip)}</span>`)
              .join('')}</div>`
          : '&mdash;';
        return `<div>
        <dt>${field.label}</dt>
        <dd>${content}</dd>
      </div>`;
      }
      const highlightTimeJail = field.key === 'time_jail' && isInTimeJail(value);
      const safeDisplay = displayValue ? escapeHTML(displayValue) : '&mdash;';
      const decoratedDisplay =
        highlightTimeJail && displayValue
          ? `<span class="time-jail-badge">${safeDisplay}</span>`
          : safeDisplay;
      return `<div>
        <dt>${field.label}</dt>
        <dd>${decoratedDisplay}</dd>
      </div>`;
    })
    .join('');
};

const collectFormData = () => {
  const scope = modalForm || modalEl;
  if (!scope) return {};
  const inputs = scope.querySelectorAll('input[data-field], textarea[data-field], select[data-field]');
  const data = {};
  inputs.forEach((input) => {
    data[input.dataset.field] = input.value;
    if (input.dataset.field === 'character_name') data.name = input.value;
  });
  return data;
};

const handleSave = async () => {
  if (!currentCharacterId) return;
  if (!canEditCharacter(getCharacterById(currentCharacterId))) {
    alert('You can only edit your own characters.');
    return;
  }
  const updates = collectFormData();
  updates.portrait_url = currentPortraitUrl;
  const currentCharacter = getCharacterById(currentCharacterId);
  const playerName = String(updates.player || '').trim();
  const selectedPlayer = getPlayerByName(getState().players, playerName);
  if (!playerName) {
    updates.player = '';
    updates.discord_id = '';
  } else if (selectedPlayer) {
    updates.player = selectedPlayer.name;
    updates.discord_id = selectedPlayer.discord_id || '';
  } else {
    updates.player = playerName;
    updates.discord_id = currentCharacter?.discord_id || '';
  }
  try {
    await updateCharacter(currentCharacterId, updates);
    updateCharacterLocal(currentCharacterId, {
      ...updates,
      name: updates.character_name ?? updates.name ?? currentCharacter?.name
    });
    setModalEdit(false);
    await maybeLogLevelUp(currentCharacter, updates);
    alert('Character updated.');
  } catch (error) {
    alert(`Save failed: ${error.message}`);
  }
};

const maybeLogLevelUp = async (previousCharacter, updates) => {
  const previousLevel = getHighestCharacterLevel(previousCharacter?.level, NaN);
  const newLevel = getHighestCharacterLevel(updates.level, NaN);
  if (!Number.isFinite(previousLevel) || !Number.isFinite(newLevel) || newLevel <= previousLevel) return;
  try {
    const event = await createLevelUpEvent({
      character_name: updates.character_name ?? updates.name ?? previousCharacter?.name,
      from_level: previousCharacter?.level || '',
      to_level: updates.level
    });
    addLevelUpEventLocal(event);
  } catch (error) {
    console.error(`Failed to log level-up event: ${error.message}`);
  }
};

const handleDeleteCharacter = async () => {
  const character = currentCharacterId ? getCharacterById(currentCharacterId) : null;
  if (!character) return;
  if (!canEditCharacter(character)) {
    alert('You can only delete your own characters.');
    return;
  }

  const confirmed = window.confirm(`Delete ${character.name || 'this character'}?`);
  if (!confirmed) return;

  try {
    setLoading(true);
    await deleteCharacter(character.id);
    removeCharacterLocal(character.id);
    alert('Character deleted.');
  } catch (error) {
    alert(`Delete failed: ${error.message}`);
  } finally {
    setLoading(false);
  }
};

const closeModal = () => {
  modalEl.close();
  setBodyScrollLock(false);
  closeModalState();
};

const updateActionButtons = (isEditing) => {
  const character = currentCharacterId ? getCharacterById(currentCharacterId) : null;
  const canEdit = canEditCharacter(character);
  if (modalEditBtn) {
    modalEditBtn.style.display = canEdit ? '' : 'none';
    if (isEditing) {
      modalEditBtn.textContent = 'Save';
      modalEditBtn.onclick = handleSave;
    } else {
      modalEditBtn.textContent = 'Edit';
      modalEditBtn.onclick = () => setModalEdit(true);
    }
  }
  if (modalEditBar) modalEditBar.classList.toggle('hidden', !isEditing);
  if (modalDeleteCharacterBtn) {
    modalDeleteCharacterBtn.style.display = isEditing && canEdit && character ? 'inline-flex' : 'none';
  }
  if (modalDangerBar) modalDangerBar.style.display = isEditing && canEdit && character ? 'flex' : 'none';
  modalPortraitUpload?.classList.toggle('editable', isEditing && canEdit);
};

const initCollapsibles = () => {
  const root = modalForm || modalEl;
  if (!root) return;
  root.querySelectorAll('.collapsible-toggle').forEach((btn) => {
    btn.setAttribute('aria-expanded', 'true');
  });
  root.addEventListener('click', (event) => {
    const toggle = event.target.closest('.collapsible-toggle');
    if (!toggle || !root.contains(toggle)) return;
    event.preventDefault();
    event.stopPropagation();
    const wrapper = toggle.closest('.collapsible');
    if (!wrapper) return;
    const collapsed = wrapper.classList.toggle('collapsed');
    toggle.setAttribute('aria-expanded', String(!collapsed));
  });
};

const initModalPills = () => {
  const root = modalForm || modalEl;
  if (!root) return;

  // Toggle dropdown panel
  root.addEventListener('click', (event) => {
    const toggle = event.target.closest('[data-pill-summary]');
    if (toggle && root.contains(toggle)) {
      event.preventDefault();
      event.stopPropagation();
      const dropdown = toggle.closest('.pill-dropdown');
      if (!dropdown) return;
      const list = dropdown.querySelector('.pill-list');
      if (!list) return;
      const isOpen = !list.hasAttribute('hidden');
      if (isOpen) {
        list.setAttribute('hidden', '');
        toggle.classList.remove('open');
      } else {
        list.removeAttribute('hidden');
        toggle.classList.add('open');
      }
      return;
    }
  });

  // Handle pill selection
  root.addEventListener('click', (event) => {
    const pill = event.target.closest('.pill[data-pill-field]');
    if (!pill || !root.contains(pill)) return;
    event.preventDefault();
    const fieldKey = pill.getAttribute('data-pill-field');
    const value = pill.getAttribute('data-pill-value');
    const hidden = root.querySelector(`input[type="hidden"][data-field="${fieldKey}"]`);
    if (!hidden || !value) return;
    const container = pill.closest('[data-pill-container]');
    const multiple = container?.dataset.pillMode !== 'single';
    const limit = Number(container?.dataset.pillLimit || '0');
    const set = multiple ? parsePillValues(hidden.value) : parseSinglePillValue(hidden.value);
    if (multiple) {
      if (set.has(value)) {
        set.delete(value);
      } else {
        if (limit > 0 && set.size >= limit) {
          const label = container?.dataset.pillLabel || 'options';
          alert(`You can only select ${limit} ${label}.`);
          return;
        }
        set.add(value);
      }
      hidden.value = Array.from(set).join(', ');
    } else {
      set.clear();
      set.add(value);
      hidden.value = value;
      const dropdown = pill.closest('.pill-dropdown');
      const list = dropdown?.querySelector('.pill-list');
      const summaryButton = dropdown?.querySelector('[data-pill-summary]');
      if (list) list.setAttribute('hidden', '');
      if (summaryButton) summaryButton.classList.remove('open');
    }
    updatePillSummaryDisplay(container, set);
  });

  // Close dropdown if clicking outside
  document.addEventListener('click', (event) => {
    if (!modalEl.open) return;
    if (modalForm && modalForm.contains(event.target)) return;
    modalEl.querySelectorAll('.pill-dropdown').forEach((dropdown) => {
      const list = dropdown.querySelector('.pill-list');
      const button = dropdown.querySelector('[data-pill-summary]');
      if (list) list.setAttribute('hidden', '');
      if (button) button.classList.remove('open');
    });
  });
};

const handlePortraitInputChange = async (event) => {
  const file = event.target.files?.[0];
  if (!file) return;
  const hint = modalPortraitPlate?.querySelector('.plate-hint');
  if (hint) hint.textContent = 'Uploading…';
  try {
    currentPortraitUrl = await uploadPortrait(file);
    setPlateImage(modalPortraitPlate, currentPortraitUrl);
  } catch (error) {
    alert(`Portrait upload failed: ${error.message}`);
    if (hint) hint.textContent = 'No portrait';
  } finally {
    event.target.value = '';
  }
};

export const initModal = () => {
  modalCancelEditBtn?.addEventListener('click', () => setModalEdit(false));
  modalCloseBtn?.addEventListener('click', closeModal);
  modalDeleteCharacterBtn?.addEventListener('click', handleDeleteCharacter);
  modalPortraitInput?.addEventListener('change', handlePortraitInputChange);
  modalForm?.addEventListener('click', (event) => event.stopPropagation());
  initCollapsibles();
  initModalPills();
  updateActionButtons(false);

  subscribe(({ modal }) => {
    const character = modal.characterId ? getCharacterById(modal.characterId) : null;
    currentCharacterId = character?.id ?? null;

    if (!character) {
      if (modalEl.open) {
        modalEl.close();
        setBodyScrollLock(false);
      }
      return;
    }

    if (!modalEl.open) {
      modalEl.showModal();
      setBodyScrollLock(true);
    }

    modalName.textContent = character.name || 'Unnamed';
    modalPlayer.textContent = character.player || 'Unknown Player';

    currentPortraitUrl = character.portrait_url || '';
    setPlateImage(modalPortraitPlate, currentPortraitUrl);
    modalPortraitPlate?.classList.toggle('dead', isDeadStatus(character.status));

    renderGrid(modalOverview, OVERVIEW_FIELDS, character, modal.edit);
    renderGrid(modalStats, STAT_FIELDS, character, modal.edit);
    renderGrid(modalBackground, BACKGROUND_FIELDS, character, modal.edit);
    syncAllPillSummaries();
    updateActionButtons(modal.edit);
  });
};
